// Centralized EPM validation constants, regular expressions, and helper rules.

export const EPM_LIMITS = {
    // Single-line and multi-line defaults
    DEFAULT_SINGLE_LINE_MAX: 200,
    DEFAULT_MULTI_LINE_MAX: 2000,

    // Step 0 / Modal (New Idea)
    TEST_KEY_MIN: 1,
    TEST_KEY_MAX: 40,
    IDEA_TITLE_MIN: 3,
    IDEA_TITLE_MAX: 120,
    IDEA_DESC_MAX: 2000,

    // Step 1 (Idea)
    HYPOTHESIS_MIN: 20,
    HYPOTHESIS_MAX: 1000,
    LOCATION_TEXT_MAX: 200,
    EVIDENCE_MAX_COUNT: 3,
    EVIDENCE_ITEM_MAX: 500,
    MOCKUPS_MAX_COUNT: 10,

    // Step 2 (Priority)
    SCORE_MIN: 1,
    SCORE_MAX: 10,
    DECISION_REASON_MIN: 5,
    DECISION_REASON_MAX: 500,

    // Step 3 (Plan)
    PRIMARY_METRIC_MIN: 2,
    PRIMARY_METRIC_MAX: 100,
    SECONDARY_METRICS_MAX_COUNT: 5,
    SECONDARY_METRIC_MAX: 100,

    // Step 4 (Build & QA)
    VARIANTS_MAX_COUNT: 10,
    VARIANT_DETAILS_MAX: 200,
    ONE_OFF_CHECKLIST_NAME_MIN: 1,
    ONE_OFF_CHECKLIST_NAME_MAX: 150,
    ONE_OFF_CHECKLIST_DESC_MAX: 500,

    // Step 5 (Schedule)
    MIN_RUN_DAYS: 1,
    MAX_RECOMMENDED_WEEKS: 12,

    // Step 6 (Result)
    RATIONALE_MIN: 10,
    RATIONALE_MAX: 2000,
    WHAT_WE_NOW_BELIEVE_MIN: 10,
    WHAT_WE_NOW_BELIEVE_MAX: 1000,
    RESULT_LINK_MAX: 2048,
    WHAT_TO_TEST_NEXT_MAX: 500,
    WHAT_HAPPENED_MAX: 1000,
    WHY_WE_THINK_MAX: 1000,

    // Settings
    TAG_NAME_MIN: 1,
    TAG_NAME_MAX: 40,
    LOCATION_LABEL_MAX: 40,
    LOCATION_KEY_MAX: 40,
    LOCATION_OPTION_MAX: 60,
    CHECKLIST_NAME_MAX: 150,
    CHECKLIST_DESC_MAX: 500,
};

export const EPM_PATTERNS = {
    // 1-40 chars, start with alphanum, allowed: space, dot, underscore, slash, hyphen
    TEST_KEY: /^[A-Za-z0-9][A-Za-z0-9 ._/-]{0,39}$/,
    // Tag: 1-40 lowercase letters/digits, separated by hyphen or slash (allows a/b, cta-copy)
    TAG_NAME: /^[a-z0-9]+(?:[-/][a-z0-9]+)*$/,
    // Location key: lowercase letter start, alphanum + underscore
    LOCATION_KEY: /^[a-z][a-z0-9_]{0,39}$/,
    // URL beginning with http:// or https://
    HTTP_URL: /^https?:\/\/\S+$/,
    // Result link: http(s) URL with valid authority and path
    RESULT_LINK: /^https?:\/\/[^\s/$.?#][^\s]*$/,
};

/**
 * Checks whether a value contains at least one non-whitespace character.
 */
export const hasNonWhitespace = (val) => {
    return typeof val === 'string' && /\S/.test(val);
};

/**
 * Validates that text length is within min and max boundaries after trimming.
 */
export const isTrimmedLengthBetween = (val, min = 0, max = Infinity) => {
    if (typeof val !== 'string') return min === 0;
    const len = val.trim().length;
    return len >= min && len <= max;
};

/**
 * Safely tests a string value against an optional regex pattern.
 * If no pattern is provided, it is considered valid.
 */
export const testPattern = (pattern, value) => {
    if (!pattern || !value) return true;
    try {
        const regex = new RegExp(pattern);
        return regex.test(value);
    } catch (e) {
        console.warn('Invalid regex pattern configured:', pattern, e);
        return true;
    }
};

/**
 * Validates whether a string is a valid regex syntax.
 */
export const isValidRegexString = (pattern) => {
    if (!pattern) return true;
    try {
        new RegExp(pattern);
        return true;
    } catch (e) {
        return false;
    }
};

